import { useState } from 'react';
import { usePredictionMarketEventsFindLobby } from '@/lib/endpoints';
import { useAppSearchParams, TRENDING_CATEGORY_CODE } from '@/hooks/useAppSearchParams';
import type { OrderSide } from '@/analytics';
import type { EventCardDto, MarketOptionDto } from '@/lib/models';
import { BinaryQuestionCard } from './BinaryQuestionCard';
import { BinaryQuestionCardSkeleton } from './BinaryQuestionCardSkeleton';
import { BuyInPopup, type BuyMarketContext } from './BuyInPopup';
import { SportsMoneylineCard } from '../SportsMarkets/SportsMoneylineCard';

/** Popup target carrying the resolved option context + opening side. */
interface BuyTarget {
  context: BuyMarketContext;
  side: OrderSide;
}

const SKELETON_COUNT = 6;

/**
 * Container + responsive card layout shared by the loaded feed and its
 * skeleton: a stacked list on mobile, a 2-column grid at `md` and 3 at `xl`
 * (the lobby card-grid convention, cf. `CategorySections`), with the width
 * cap opening from 760px to 1300px at `xl` to fit the columns.
 */
const FEED_CONTAINER = 'w-full max-w-[760px] xl:max-w-[1300px] px-3.5 md:px-0 pb-6';
const FEED_GRID = 'flex flex-col gap-2 md:grid md:grid-cols-2 md:items-stretch md:gap-3 lg:gap-4 xl:grid-cols-3';

/**
 * Lobby binary-question feed (ARC-82 §4).
 *
 * Fetches the lobby via `usePredictionMarketEventsFindLobby`, scoped by the
 * active `?category` / `?subCategory` (the synthetic `TRENDING` code is treated
 * as "no filter"). Renders one {@link BinaryQuestionCard} per event and owns
 * the shared buy-in popup opened from any card's probability button (§4.3).
 */
export function BinaryLobbyFeed() {
  const { category, subCategory } = useAppSearchParams();
  const categoryCode = category && category !== TRENDING_CATEGORY_CODE ? category : undefined;
  const subCategoryCode = subCategory ?? undefined;

  const { data, isLoading, isError } = usePredictionMarketEventsFindLobby({ categoryCode, subCategoryCode });
  const [buyTarget, setBuyTarget] = useState<BuyTarget | null>(null);

  const events = data?.events ?? [];

  function handleOpenBuy(event: EventCardDto, option: MarketOptionDto, side: OrderSide) {
    setBuyTarget({ context: { ...option, eventName: event.name }, side });
  }

  if (isError) {
    return (
      <div className="w-full max-w-[760px] px-3.5 md:px-0 py-6 text-center text-sm text-muted">Failed to load markets.</div>
    );
  }

  if (isLoading) {
    return (
      <div className={`${FEED_CONTAINER} ${FEED_GRID}`}>
        {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
          <BinaryQuestionCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="w-full max-w-[760px] px-3.5 md:px-0 py-6 text-center text-sm text-muted">
        No markets in this category yet.
      </div>
    );
  }

  return (
    <div className={FEED_CONTAINER}>
      <div className={FEED_GRID}>
        {events.map((event) =>
          event.sportsMoneyline ? (
            <SportsMoneylineCard
              key={event.id}
              event={event}
              moneyline={event.sportsMoneyline}
              onOpenBuy={(option, side) => handleOpenBuy(event, option, side)}
            />
          ) : (
            <BinaryQuestionCard
              key={event.id}
              event={event}
              onOpenBuy={(option, side) => handleOpenBuy(event, option, side)}
            />
          ),
        )}
      </div>

      <BuyInPopup
        market={buyTarget?.context ?? null}
        initialSide={buyTarget?.side ?? 'yes'}
        surface="home.card"
        onClose={() => setBuyTarget(null)}
      />
    </div>
  );
}
